import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FaMapMarkerAlt, FaSearch, FaSpinner, FaTimes } from 'react-icons/fa';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import {
  buildAddressFormPatchFromResolved,
  resolveFormAddressFromCoords,
  searchAddressSuggestions,
} from './locationFallbackUtils';

// Fix CRA/webpack broken default marker paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 }; // India
const DEFAULT_ZOOM = 5;
const PIN_ZOOM = 16;

export default function PartnerLocationFallback({
  open,
  onClose,
  onApply,
  placeLabel = 'shop',
  initialCenter = null,
}) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [markerPos, setMarkerPos] = useState(() => (
    initialCenter && Number.isFinite(initialCenter.lat) && Number.isFinite(initialCenter.lng)
      ? { lat: initialCenter.lat, lng: initialCenter.lng }
      : null
  ));

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const searchAbortRef = useRef(null);
  const searchTimerRef = useRef(null);

  const syncMarker = useCallback((lat, lng, { fly = true } = {}) => {
    if (!mapRef.current) return;
    const next = { lat: Number(lat), lng: Number(lng) };
    if (!Number.isFinite(next.lat) || !Number.isFinite(next.lng)) return;
    setMarkerPos(next);
    if (!markerRef.current) {
      markerRef.current = L.marker([next.lat, next.lng], { draggable: true }).addTo(mapRef.current);
      markerRef.current.on('dragend', () => {
        const pos = markerRef.current.getLatLng();
        setMarkerPos({ lat: pos.lat, lng: pos.lng });
        setError('');
      });
    } else {
      markerRef.current.setLatLng([next.lat, next.lng]);
    }
    if (fly) {
      mapRef.current.setView([next.lat, next.lng], Math.max(mapRef.current.getZoom(), PIN_ZOOM));
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    if (!mapContainerRef.current || mapRef.current) return undefined;

    const start = initialCenter && Number.isFinite(initialCenter.lat) && Number.isFinite(initialCenter.lng)
      ? initialCenter
      : DEFAULT_CENTER;
    const zoom = initialCenter ? PIN_ZOOM : DEFAULT_ZOOM;

    const map = L.map(mapContainerRef.current, {
      center: [start.lat, start.lng],
      zoom,
      scrollWheelZoom: true,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    map.on('click', (e) => {
      syncMarker(e.latlng.lat, e.latlng.lng, { fly: false });
      setError('');
    });

    mapRef.current = map;

    if (initialCenter && Number.isFinite(initialCenter.lat) && Number.isFinite(initialCenter.lng)) {
      syncMarker(initialCenter.lat, initialCenter.lng);
    }

    // Leaflet needs a size refresh after modal layout
    const t = setTimeout(() => map.invalidateSize(), 80);

    return () => {
      clearTimeout(t);
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setSuggestions([]);
      setSearching(false);
      setConfirming(false);
      setError('');
      setMarkerPos(
        initialCenter && Number.isFinite(initialCenter.lat) && Number.isFinite(initialCenter.lng)
          ? { lat: initialCenter.lat, lng: initialCenter.lng }
          : null
      );
    }
  }, [open, initialCenter]);

  useEffect(() => {
    if (!open) return undefined;
    if (searchAbortRef.current) searchAbortRef.current.abort();
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    const q = query.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setSearching(false);
      return undefined;
    }

    setSearching(true);
    searchTimerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      searchAbortRef.current = controller;
      try {
        const hits = await searchAddressSuggestions(q, { signal: controller.signal });
        if (!controller.signal.aborted) {
          setSuggestions(hits);
          setSearching(false);
        }
      } catch (err) {
        if (err?.name === 'AbortError') return;
        setSuggestions([]);
        setSearching(false);
      }
    }, 320);

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (searchAbortRef.current) searchAbortRef.current.abort();
    };
  }, [query, open]);

  const applyResolved = (resolved) => {
    const patch = buildAddressFormPatchFromResolved(resolved);
    if (!patch) {
      setError('Could not resolve that location. Try another address or map pin.');
      return;
    }
    onApply(patch);
    onClose();
  };

  const handleSelectSuggestion = async (suggestion) => {
    setQuery(suggestion.label);
    setSuggestions([]);
    syncMarker(suggestion.lat, suggestion.lng);
    setError('');
    setConfirming(true);
    try {
      // Enrich with reverse-geocode so PIN / street / city fill existing fields
      const detailed = await resolveFormAddressFromCoords(suggestion.lat, suggestion.lng);
      applyResolved({
        houseNo: detailed?.houseNo || suggestion.houseNo,
        street: detailed?.street || suggestion.street,
        landmark: detailed?.landmark || suggestion.landmark,
        city: detailed?.city || suggestion.city,
        state: detailed?.state || suggestion.state,
        pinCode: detailed?.pinCode || suggestion.pinCode,
        label: suggestion.label || detailed?.label,
        lat: suggestion.lat,
        lng: suggestion.lng,
      });
    } catch {
      applyResolved(suggestion);
    } finally {
      setConfirming(false);
    }
  };

  const handleConfirmMap = async () => {
    if (!markerPos) {
      setError('Drop a pin on the map or search for your address first.');
      return;
    }
    setConfirming(true);
    setError('');
    try {
      const resolved = await resolveFormAddressFromCoords(markerPos.lat, markerPos.lng);
      if (!resolved) {
        setError('Could not reverse-geocode this pin. Try a nearby spot.');
        return;
      }
      applyResolved({
        ...resolved,
        lat: markerPos.lat,
        lng: markerPos.lng,
      });
    } catch {
      setError('Could not reverse-geocode this pin. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  if (!open) return null;

  return (
    <Overlay role="dialog" aria-modal="true" aria-labelledby="desktop-location-fallback-title">
      <Modal>
        <ModalHeader>
          <HeaderCopy>
            <Eyebrow>Set {placeLabel} location</Eyebrow>
            <Title id="desktop-location-fallback-title">
              Exact GPS wasn&apos;t available
            </Title>
            <Subtitle>
              Search for your {placeLabel} address, or drop a pin on the map, then confirm.
            </Subtitle>
          </HeaderCopy>
          <CloseBtn type="button" onClick={onClose} aria-label="Close">
            <FaTimes />
          </CloseBtn>
        </ModalHeader>

        {error && <ErrorBanner>{error}</ErrorBanner>}

        <OptionBlock>
          <OptionLabel>
            <FaSearch />
            Search address
          </OptionLabel>
          <SearchWrap>
            <SearchInput
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search your ${placeLabel} address…`}
              autoComplete="off"
              disabled={confirming}
            />
            {(searching || confirming) && (
              <SearchSpinner><FaSpinner className="fa-spin" /></SearchSpinner>
            )}
            {suggestions.length > 0 && !confirming && (
              <SuggestionList>
                {suggestions.map((item) => (
                  <SuggestionItem key={item.id} type="button" onClick={() => handleSelectSuggestion(item)}>
                    <FaMapMarkerAlt />
                    <span>{item.label}</span>
                  </SuggestionItem>
                ))}
              </SuggestionList>
            )}
          </SearchWrap>
        </OptionBlock>

        <Divider>
          <span>or</span>
        </Divider>

        <OptionBlock>
          <OptionLabel>
            <FaMapMarkerAlt />
            Pin on map
          </OptionLabel>
          <MapHint>Click the map or drag the marker, then confirm.</MapHint>
          <MapBox ref={mapContainerRef} />
          <ConfirmBtn type="button" onClick={handleConfirmMap} disabled={confirming || !markerPos}>
            {confirming ? (
              <>
                <FaSpinner className="fa-spin" />
                Confirming…
              </>
            ) : (
              'Confirm location'
            )}
          </ConfirmBtn>
        </OptionBlock>
      </Modal>
    </Overlay>
  );
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(6, 10, 16, 0.72);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Modal = styled.div`
  width: min(560px, 100%);
  max-height: min(90vh, 820px);
  overflow: auto;
  background: var(--pp-surface, var(--sp-bg-card, #fff));
  color: var(--pp-text, var(--sp-ink, #0f172a));
  border-radius: 14px;
  border: 1px solid var(--pp-border, var(--sp-border, #e2e8f0));
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
  padding: 18px 18px 20px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;

const HeaderCopy = styled.div`
  flex: 1;
  min-width: 0;
`;

const Eyebrow = styled.p`
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--pp-brand, #00aeef);
`;

const Title = styled.h3`
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--pp-text, var(--sp-ink, #0f172a));
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: var(--pp-muted, var(--sp-ink-2, #64748b));
`;

const CloseBtn = styled.button`
  border: 1px solid var(--pp-border, var(--sp-border, #e2e8f0));
  background: var(--pp-input-muted-bg, var(--sp-bg-surface, transparent));
  color: var(--pp-muted, var(--sp-ink-3, #64748b));
  cursor: pointer;
  padding: 8px;
  line-height: 1;
  border-radius: 8px;
  flex-shrink: 0;

  &:hover {
    color: var(--pp-text, var(--sp-ink, #0f172a));
  }
`;

const ErrorBanner = styled.div`
  background: var(--pp-error-bg, #fef2f2);
  color: var(--pp-error-text, #b91c1c);
  border: 1px solid var(--pp-error-border, #fecaca);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  margin-bottom: 12px;
`;

const OptionBlock = styled.div`
  margin-bottom: 4px;
`;

const OptionLabel = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--pp-text, var(--sp-ink, #0f172a));

  svg {
    color: var(--pp-brand, #00aeef);
  }
`;

const SearchWrap = styled.div`
  position: relative;
  margin-bottom: 4px;
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 36px 12px 12px;
  border: 1px solid var(--pp-border, var(--sp-border, #e2e8f0));
  border-radius: 10px;
  font-size: 14px;
  background: var(--pp-input-muted-bg, var(--sp-bg-surface, #fff));
  color: var(--pp-text, var(--sp-ink, #0f172a));
  caret-color: var(--pp-text, var(--sp-ink, #0f172a));

  &::placeholder {
    color: var(--pp-muted, var(--sp-ink-3, #94a3b8));
  }

  &:focus {
    outline: none;
    border-color: var(--pp-brand, #00aeef);
    box-shadow: 0 0 0 3px var(--pp-brand-bg, rgba(0, 174, 239, 0.15));
  }
`;

const SearchSpinner = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--pp-muted, #64748b);
  font-size: 13px;
`;

const SuggestionList = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  z-index: 5;
  background: var(--pp-surface, var(--sp-bg-card, #fff));
  border: 1px solid var(--pp-border, var(--sp-border, #e2e8f0));
  border-radius: 10px;
  max-height: 220px;
  overflow: auto;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.28);
`;

const SuggestionItem = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  color: var(--pp-text, var(--sp-ink, #0f172a));
  border-bottom: 1px solid var(--pp-border, var(--sp-border, #f1f5f9));

  svg {
    margin-top: 2px;
    flex-shrink: 0;
    color: var(--pp-brand, #00aeef);
  }

  &:hover {
    background: var(--pp-brand-bg, #f0f9ff);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 14px 0 16px;
  color: var(--pp-muted, var(--sp-ink-3, #64748b));
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--pp-border, var(--sp-border, #e2e8f0));
  }
`;

const MapHint = styled.p`
  margin: 0 0 8px;
  font-size: 12px;
  color: var(--pp-muted, var(--sp-ink-3, #64748b));
`;

const MapBox = styled.div`
  width: 100%;
  height: 280px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--pp-border, var(--sp-border, #e2e8f0));
  margin-bottom: 12px;
  z-index: 0;
`;

const ConfirmBtn = styled.button`
  width: 100%;
  padding: 12px 14px;
  border: none;
  border-radius: 10px;
  background: var(--pp-brand, #00aeef);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    filter: brightness(1.05);
  }
`;
